import { Fragment, useId, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { QuestionConceptRef, QuestionListItem, QuestionTaxonomyTag } from '@course-authoring/shared-types';
import { ModalShell } from '../ModalShell';
import { QUESTION_BANK_BROWSE_COPY as COPY, QUESTION_BANK_TYPE_LABELS } from './questionBankBrowseCopy';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3 Sep 2026" from an ISO timestamp; the raw value when it does not parse. */
export function formatAddedDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export interface QuestionBankDetailProps {
  styles: QuestionBankBrowseStyles;
  /** The open question; null keeps the dialog closed (it stays mounted to restore focus). */
  item: QuestionListItem | null;
  /** The page's 560px or the picker's 680px. */
  width: string;
  /** The footer's contents for the open question; null leaves the dialog without a footer. */
  footer: (item: QuestionListItem) => ReactNode;
  onClose: () => void;
  /** A framework child label was activated: filter the list to it. */
  onFilterLabel: (tag: QuestionTaxonomyTag) => void;
  /** A concept was activated: filter the list to it. */
  onFilterConcept: (concept: QuestionConceptRef) => void;
}

/**
 * One question over the page or the picker: the question, its identity
 * line, its concepts (each a link that filters the list to it), one row per
 * framework it is tagged in (the child label filters the list), its usage,
 * and the caller's footer. A sibling of the page or picker dialog, so Escape
 * and a backdrop click close only this layer.
 */
export function QuestionBankDetail({ styles, item, width, footer, onClose, onFilterLabel, onFilterConcept }: QuestionBankDetailProps) {
  const titleId = useId();
  const b = styles.browse;
  const footerContent = item ? footer(item) : null;
  return (
    <ModalShell isOpen={item !== null} onClose={onClose} ariaLabelledBy={titleId} width={width} centerOnContent presentation="admin">
      {item ? (
        <>
          <div style={styles.centerstage.dialogHeader}>
            <h2 id={titleId} style={b.detailTitle}>{item.questionTextPlain}</h2>
            <button type="button" aria-label={COPY.close} onClick={onClose} style={styles.centerstage.iconButton}>
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '16px' }} />
            </button>
          </div>
          <div style={styles.centerstage.dialogScroll}>
            <div style={b.detailBody}>
              <p style={b.detailIdentity} data-testid="question-identity">
                {`Q${item.questionDefinitionId} · ${QUESTION_BANK_TYPE_LABELS[item.blockType] ?? item.blockType} · ${item.purpose}`}
              </p>
              <div style={b.detailRows}>
                {item.concepts.length > 0 ? (
                  <dl style={b.detailRow}>
                    <dt style={b.detailLabel}>{COPY.concepts}</dt>
                    <dd style={b.detailValue}>
                      {item.concepts.map((concept, index) => (
                        <Fragment key={concept.id}>
                          {index > 0 ? ' · ' : null}
                          <button type="button" style={b.detailLink} onClick={() => onFilterConcept(concept)}>{concept.name}</button>
                        </Fragment>
                      ))}
                    </dd>
                  </dl>
                ) : null}
                {item.taxonomyTags.length === 0 ? <p style={b.untagged}>{COPY.untagged}</p> : null}
                {item.taxonomyTags.map((tag) => (
                  <dl key={tag.labelId} style={b.detailRow}>
                    <dt style={b.detailLabel}>{tag.displayName}</dt>
                    <dd style={b.detailValue}>
                      {`${tag.parentLabel}: `}
                      <button type="button" style={b.detailLink} onClick={() => onFilterLabel(tag)}>{tag.childLabel}</button>
                    </dd>
                  </dl>
                ))}
                <dl style={b.detailRow}>
                  <dt style={b.detailLabel}>{COPY.usage}</dt>
                  <dd style={b.detailValue}>
                    {[COPY.responses(item.responseCount), COPY.usedIn(item.usageCount), COPY.added(formatAddedDate(item.createdAt))].join(' · ')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          {footerContent ? <div style={b.detailFooter}>{footerContent}</div> : null}
        </>
      ) : null}
    </ModalShell>
  );
}
